/**
 * 댓글 링크 규약 SSOT (JCG-3603 댓글 딥링크 · JCG-2706 내 댓글 모아보기 · JCG-3601 답글 알림 벨 공용).
 *
 * 클라이언트·서버 어디서나 import 가능한 순수 모듈이다(외부 데이터·DOM 접근 없음).
 *
 * - 댓글 DOM 앵커 id: `comment-{comments.id}` (`getCommentAnchorId`)
 * - 딥링크: `{pagePath}#comment-{comments.id}` (`buildCommentDeepLink`)
 *   해시만 쓰는 이유: 댓글 섹션은 클라이언트에서 server action 으로 로드되므로 서버가 대상을 알 필요가 없고,
 *   `?comment=` 를 섞으면 `useSearchParams` Suspense 경계와 URL 정규화 부담이 생긴다.
 * - 진입 시 대상 추출: `parseCommentTargetId(window.location.hash)`
 *
 * 댓글 `slug` → 페이지 경로 변환은 두 단계다.
 * 1. 일반 글(`theory/…`, `coding/…`, `past-exam/…`, `final-mock-exam` 등)은 `/{slug}` 로 정적 변환된다
 *    (`resolveStaticCommentPagePath`). PostLayout 이 `fullSlug = "{pathPrefix}/{slug}"`, `pagePath = "/{fullSlug}"`
 *    로 만들기 때문이다.
 * 2. 포럼(`forum/{problemId}`)은 problems-index 로 scope·subject 를, 복원 게시판(`restoration/{postId}`)은
 *    DB 로 session_id 를 찾아야 하므로 서버 전용 resolver(`server-utils/comment-page-path.ts`)가 담당한다.
 *    이 모듈은 그 경우 `null` 을 돌려준다.
 */

/** 댓글 DOM 앵커 id 접두사. `CommentItem` 루트 요소의 `id` 와 딥링크 해시가 공유한다. */
export const COMMENT_ANCHOR_PREFIX = "comment-";

/** 댓글 섹션 래퍼 앵커 id (PostLayout 이 `CommentSection` 래퍼에 부여, ArticleScrollStepper 와 공유). */
export const COMMENTS_SECTION_ANCHOR_ID = "comments";

/** 포럼 댓글 slug 접두사 — `ForumCommentSection` 이 `forum/{problemId}` 로 저장한다. */
export const FORUM_COMMENT_SLUG_PREFIX = "forum/";

/** 복원 게시판 댓글 slug 접두사 — `RestorationCommentSection` 이 `restoration/{postId}` 로 저장한다. */
export const RESTORATION_COMMENT_SLUG_PREFIX = "restoration/";

/** `comments.slug` 허용 문자(actions/_shared.ts `validateSlug` 와 동일 규칙). */
const COMMENT_SLUG_PATTERN = /^[a-zA-Z0-9-_/]+$/;

/** `comments.id` 는 UUID 다. 해시에서 추출할 때 임의 문자열이 앵커 id 로 쓰이지 않도록 형식을 검사한다. */
const COMMENT_ID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function isValidCommentId(commentId: string): boolean {
  return COMMENT_ID_PATTERN.test(commentId);
}

export function isForumCommentSlug(slug: string): boolean {
  return slug.startsWith(FORUM_COMMENT_SLUG_PREFIX);
}

export function isRestorationCommentSlug(slug: string): boolean {
  return slug.startsWith(RESTORATION_COMMENT_SLUG_PREFIX);
}

/** 댓글 DOM 앵커 id. `CommentItem` 루트 요소 `id` 와 딥링크 해시가 이 값을 공유한다. */
export function getCommentAnchorId(commentId: string): string {
  return `${COMMENT_ANCHOR_PREFIX}${commentId}`;
}

/**
 * 댓글 딥링크 — `{pagePath}#comment-{id}`.
 *
 * `pagePath` 는 `/theory/sw-design/ui-types` 처럼 사이트 루트 기준 절대 경로(쿼리 포함 가능)다.
 * 기존 해시가 있으면 버리고 댓글 앵커로 교체한다. `pagePath` 가 비어 있으면 `/` 로 간주한다.
 */
export function buildCommentDeepLink(pagePath: string, commentId: string): string {
  const withoutHash = pagePath.split("#")[0] || "/";
  const normalized = withoutHash.startsWith("/") ? withoutHash : `/${withoutHash}`;
  return `${normalized}#${getCommentAnchorId(commentId)}`;
}

/**
 * URL 해시(`window.location.hash`, `#` 포함·미포함 모두 허용)에서 대상 댓글 id 를 추출한다.
 * 규약(`#comment-{uuid}`)에 맞지 않으면 `null`.
 */
export function parseCommentTargetId(hash: string | null | undefined): string | null {
  if (!hash) return null;
  let value = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    value = decodeURIComponent(value);
  } catch {
    return null;
  }
  if (!value.startsWith(COMMENT_ANCHOR_PREFIX)) return null;
  const commentId = value.slice(COMMENT_ANCHOR_PREFIX.length);
  return isValidCommentId(commentId) ? commentId : null;
}

/**
 * 정적으로 변환 가능한 댓글 slug → 페이지 경로.
 *
 * - 일반 글 slug(`theory/sw-design/ui-types`, `final-mock-exam`)는 `/{slug}`.
 * - 포럼·복원 게시판 slug 는 서버 조회가 필요하므로 `null`(서버 resolver 로 위임).
 * - 허용 문자를 벗어나거나 비어 있으면 `null`.
 */
export function resolveStaticCommentPagePath(slug: string): string | null {
  const trimmed = slug.trim().replace(/^\/+/, "").replace(/\/+$/, "");
  if (!trimmed || !COMMENT_SLUG_PATTERN.test(trimmed)) return null;
  if (isForumCommentSlug(trimmed) || isRestorationCommentSlug(trimmed)) return null;
  return `/${trimmed}`;
}
